import { createStreamRequestError, getStreamRetryDelayMs, isRetryableStreamRequestError, parseStreamRetryAfterMs } from './idempotency';

export interface ProcessingProgressScope {
  discover?: boolean;
  index_id?: string;
  video_ids?: string[];
  run_ids?: string[];
  import_job_ids?: string[];
  export_ids?: string[];
}

export type ProgressConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'unavailable' | 'idle' | 'stopped';
export type ProgressKind = 'video' | 'run' | 'import_job' | 'export';
export interface ProgressMessage {
  type: string;
  epoch: string;
  kind?: ProgressKind;
  id?: string;
  version?: string;
  state?: Record<string, any>;
  idle?: boolean;
  server_time?: string;
  source_status?: string;
}

const HEALTHY_RETRY_RESET_MS = 60000;
// The backend rotates at five minutes; allow a small scheduling margin.
const PLANNED_ROTATION_MIN_MS = 295000;

/** One bounded fetch stream. The store, rather than individual views, owns it. */
export function subscribeProgressTransport(options: {
  url: string;
  scope: ProcessingProgressScope;
  getToken: () => Promise<string>;
  onMessage: (message: ProgressMessage) => void;
  onConnection: (state: ProgressConnectionState) => void;
  onClosed: (reason: 'idle' | 'stopped' | 'error') => void;
}): () => void {
  let stopped = false;
  let controller: AbortController | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let wake: (() => void) | undefined;
  const stream = { idle: false, failures: 0, watchdog: undefined as ReturnType<typeof setTimeout> | undefined };
  const sleep = (delay: number) => new Promise<void>((resolve) => {
    wake = () => { clearTimeout(timer); timer = undefined; wake = undefined; resolve(); };
    timer = setTimeout(wake, delay);
  });
  const run = async () => {
    let finalReason: 'idle' | 'stopped' | 'error' = 'error';
    try {
      while (!stopped && !stream.idle) {
        let retryDelay = 1000;
        const attempt = { startedAt: Date.now(), healthySince: null as number | null };
        let ready = false;
        let timedOut = false;
        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
        try {
          options.onConnection(stream.failures ? 'reconnecting' : 'connecting');
          const token = await options.getToken();
          if (stopped) break;
          attempt.startedAt = Date.now();
          const attemptController = new AbortController();
          controller = attemptController;
          const armWatchdog = () => {
            clearTimeout(stream.watchdog);
            stream.watchdog = setTimeout(() => { timedOut = true; attemptController.abort(); }, 60000);
          };
          armWatchdog();
          const response = await fetch(options.url, {
            method: 'POST',
            headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(options.scope), signal: attemptController.signal,
          });
          if (!response.ok || !response.body) {
            throw createStreamRequestError(`Progress connection failed (${response.status}).`, {
              status: response.status,
              retryable: response.status === 429 || response.status >= 500,
              retryAfterMs: parseStreamRetryAfterMs(response.headers.get('Retry-After')) ?? undefined,
            });
          }
          reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let type = '';
          let data: string[] = [];
          const dispatch = () => {
            if (!data.length) return;
            const parsed = JSON.parse(data.join('\n'));
            const message: ProgressMessage = { ...parsed, type: type || parsed.type };
            if (!message.type?.startsWith('progress.') || typeof message.epoch !== 'string') {
              throw new Error('Invalid progress response.');
            }
            if (message.type === 'progress.unavailable') {
              throw createStreamRequestError('Live progress was interrupted.', { retryable: true });
            }
            if (message.type === 'progress.ready') {
              ready = true;
              stream.idle = message.idle === true;
              options.onConnection(stream.idle ? 'idle' : 'connected');
            } else if (message.type === 'progress.heartbeat') {
              options.onConnection(message.source_status === 'connected' ? 'connected' : 'reconnecting');
            }
            const healthy = message.type === 'progress.ready'
              || (message.type === 'progress.heartbeat' && message.source_status === 'connected');
            if (healthy) {
              if (attempt.healthySince === null) attempt.healthySince = Date.now();
              if (Date.now() - attempt.healthySince >= HEALTHY_RETRY_RESET_MS) stream.failures = 0;
            } else if (message.type === 'progress.heartbeat') {
              attempt.healthySince = null;
            }
            options.onMessage(message);
          };
          while (!stopped && !stream.idle) {
            const chunk = await reader.read();
            if (chunk.done) break;
            armWatchdog();
            buffer += decoder.decode(chunk.value, { stream: true });
            if (buffer.length > 4 * 1024 * 1024) throw new Error('Progress response exceeded its size limit.');
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const raw of lines) {
              const line = raw.replace(/\r$/, '');
              if (!line) { dispatch(); type = ''; data = []; }
              else if (line.startsWith('event:')) type = line.slice(6).trim();
              else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
            }
          }
          if (stream.idle) { finalReason = 'idle'; break; }
          if (!stopped && !ready) throw new Error('Progress connection closed before its snapshot completed.');
          if (!stopped && Date.now() - attempt.startedAt < PLANNED_ROTATION_MIN_MS) {
            throw createStreamRequestError('Progress connection closed unexpectedly.', { retryable: true });
          }
          // Planned server rotation receives a new authoritative snapshot without consuming the retry budget.
          retryDelay = 1000;
        } catch (error: any) {
          if (stopped) break;
          retryDelay = Math.min(1000 * 2 ** stream.failures, 30000);
          stream.failures += 1;
          if ((!timedOut && !isRetryableStreamRequestError(error)) || stream.failures > 5) {
            options.onConnection('unavailable');
            break;
          }
          options.onConnection('reconnecting');
          retryDelay = getStreamRetryDelayMs(error, retryDelay);
        } finally {
          clearTimeout(stream.watchdog);
          controller?.abort();
          await reader?.cancel().catch(() => undefined);
          reader?.releaseLock();
        }
        if (!stopped && !stream.idle) await sleep(retryDelay + Math.floor(Math.random() * 250));
      }
    } finally {
      clearTimeout(stream.watchdog);
      if (stopped) finalReason = 'stopped';
      options.onClosed(finalReason);
    }
  };
  void run();
  return () => { stopped = true; controller?.abort(); clearTimeout(stream.watchdog); wake?.(); };
}
